// IntonationHelpModal.jsx (full-screen overlay)
// + Added subsection: "If your curve has more gaps than the narrator’s…"
// - Full-screen (fits every time)
// - Sticky header + close button
// - Scrollable body
// - Backdrop click closes

import { useEffect } from "react";

export default function IntonationHelpModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const imgSrc = `${process.env.REACT_APP_USER_API_URL}/uploads/screens/muchwarmerthere.png`;

  return (
    <div className="fixed inset-0 z-[250]">
      {/* Backdrop (click outside closes) */}
      <button
        type="button"
        aria-label="Close help"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />

      {/* Full-screen sheet */}
      <div className="absolute inset-0 p-0 sm:p-4">
        <div
          className="relative h-full w-full bg-white sm:rounded-2xl sm:border sm:border-gray-200 sm:shadow-2xl overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Sticky Top Bar */}
          <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
            <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                  How to read the intonation curve
                </div>
                <div className="text-sm text-gray-600 truncate">
                  Example:{" "}
                  <span className="font-semibold">
                    “Wouldn’t it be much warmer there?”
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-white border border-gray-200 shadow-sm hover:bg-gray-50 transition text-sm font-semibold text-gray-800"
                >
                  <span className="text-base leading-none">✕</span>
                  <span className="hidden sm:inline">Close</span>
                </button>
              </div>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 sm:px-6 py-5 space-y-6">
              {/* Big image */}
              <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
                <img
                  src={imgSrc}
                  alt="Pitch contour visualization"
                  className="w-full h-auto block"
                />
              </div>

              {/* Explanation grid */}
              <div className="grid lg:grid-cols-2 gap-4 text-sm text-gray-700 leading-relaxed">
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                  <div className="font-semibold text-gray-900 mb-1">
                    “Wouldn’t it be much” = one movement
                  </div>
                  <div>
                    These words form one intonation group — pitch flows across them instead of
                    restarting at each word.
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                  <div className="font-semibold text-gray-900 mb-1">
                    Movement inside “warmer” and “there”
                  </div>
                  <div>
                    Pitch can change drastically inside a word. Intonation movement is not tied to
                    word boundaries.
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                  <div className="font-semibold text-gray-900 mb-1">
                    Question phrase → rising at the end
                  </div>
                  <div>
                    Because this is a question, pitch typically rises toward the end (“…there?”).
                    Practice matching that end-rise.
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                  <div className="font-semibold text-gray-900 mb-1">
                    Why the curve has gaps
                  </div>
                  <div>
                    A gap means there’s <span className="font-semibold">no measurable pitch</span>.
                    That happens during <span className="font-semibold">unvoiced sounds</span>{" "}
                    (examples: s, k, t, ch) <span className="font-semibold">and also during pauses</span>{" "}
                    (brief silence) — no vibration → pitch drops to 0 → the curve shows a gap.
                  </div>
                </div>
              </div>

              {/* ✅ NEW SUBSECTION */}
              <div className="rounded-2xl border border-gray-200 bg-white p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-xl bg-blue-50 border border-blue-100">
                    🎙️
                  </span>
                  <div className="font-semibold text-gray-900">
                    If your curve has more gaps than the narrator’s…
                  </div>
                </div>

                <div className="text-sm text-gray-700 leading-relaxed">
                  It often means you’re producing <span className="font-semibold">extra silence</span>{" "}
                  or losing voicing where the narrator stays voiced.
                </div>

                <div className="mt-3 grid md:grid-cols-2 gap-3 text-sm text-gray-700">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="font-semibold text-gray-900 mb-1">Common reasons</div>
                    <ul className="space-y-1">
                      <li>• Tiny <span className="font-semibold">pauses</span> between words (planning / hesitation)</li>
                      <li>• <span className="font-semibold">Breath resets</span> in the middle of the phrase</li>
                      <li>• Holding stops too long (long <span className="font-semibold">t / k / p</span> closures)</li>
                      <li>• <span className="font-semibold">Devoicing</span> sounds the narrator keeps voiced</li>
                    </ul>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="font-semibold text-gray-900 mb-1">What to try</div>
                    <ul className="space-y-1">
                      <li>• Aim for a smoother “carry-through” across words</li>
                      <li>• Keep air flowing gently (avoid “stop–start” speech)</li>
                      <li>• Reduce consonant “hold time” on t/k/p</li>
                      <li>• Replay word-by-word and try matching the narrator’s continuity</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-3 text-xs text-gray-500">
                  Note: pitch tracking can also briefly fail in noisy recordings, but consistent extra gaps usually reflect pauses/devoicing.
                </div>
              </div>

              <div className="text-xs text-gray-500">
                Tip: click words in the visualization to replay and connect sound with the curve.
              </div>
            </div>
          </div>

          {/* Sticky Bottom Action */}
          <div className="sticky bottom-0 z-20 bg-white border-t border-gray-200">
            <div className="px-4 sm:px-6 py-3 flex items-center justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 text-sm font-semibold shadow-sm hover:opacity-95"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}